import { useState, useEffect } from "react";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";

import { Asset } from "../../models/asset";
import { ITruckerPayPlanData } from "../../models/truckerPayPlan";

import Header from "./components/header";
import Info from "./components/info";
import Tabs, { Tab } from "./components/tabs";
import Loading from "./components/loading";
import Franquia from "./components/franquia";
import CoverageBalance from "./components/coverageBalance";

import "./styles.scss";

import { PlanStatusEnum } from "../../enums/plan";

import { fetchContractDetails, fetchVolksEssentialsContractDetails } from "../../api/fetchContractDetails";

import { monthDifference } from "../../utils/dateUtils";
import Revisions from "./components/revisions/Revisions";
import { IContractDetailsVolksEssential } from "../../models/volksPlanEssential";
import InfoPlanEssential from "./components/infoVolksEssential";
import { UserRoleEnum } from "../../enums/role";

export interface IContractDetailsSidebarProps {
  asset: Asset | null;
  show: boolean;
  userRole: UserRoleEnum;
  onClose: () => void;
  setMaintenanceGroupSidebarAsset?: React.Dispatch<React.SetStateAction<Asset | null>>
  setShowMaintenanceGroupSidebarDialog?: React.Dispatch<React.SetStateAction<boolean>>
}

export type { Tab } from "./components/tabs";

const CLOSE_ANIMATION_MS = 250;

const ContractDetailsSidebar = ({
  asset,
  show,
  userRole,
  onClose,
  setMaintenanceGroupSidebarAsset,
  setShowMaintenanceGroupSidebarDialog
}: IContractDetailsSidebarProps) => {

  const [activeTab, setActiveTab] = useState<Tab>(asset?.hasTruckerPayPlan ? "franquia" : "revisoes");
  const [isClosing, setIsClosing] = useState(false);
  const [contractDetails, setContractDetails] = useState<ITruckerPayPlanData | null>(null);
  const [contractDetailsPlanEssential, setContractDetailsPlanEssential] = useState<IContractDetailsVolksEssential | null>(null);
  const [loading, setLoading] = useState(false);
  const [hiddenFields, setHiddenFields] = useState<string[]>([]);

  useEffect(() => {
    if (show) {
      setIsClosing(false);
      setActiveTab(asset?.hasTruckerPayPlan ? "franquia" : "revisoes");
      
      setHiddenFields([]);
      if (userRole === UserRoleEnum.MANAGER) {
        setHiddenFields(["accountName"]);
      }
    }
  }, [show]);

  useEffect(() => {
    if (!show || !asset) {
      setContractDetails(null);
      setContractDetailsPlanEssential(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setContractDetails(null);
    setContractDetailsPlanEssential(null);

    const loadContractDetails = async () => {
      try {
        const requests: Promise<void>[] = [];

        if (asset.hasTruckerPayPlan) {
          requests.push(
            fetchContractDetails(asset).then((details) => {
              if (!cancelled) setContractDetails(details);
            })
          );
        }

        if (asset.hasVolksEssentialsPlan && asset.chassis) {
          requests.push(
            fetchVolksEssentialsContractDetails({ chassis: asset.chassis }).then((response) => {
              if (!cancelled) setContractDetailsPlanEssential(response);
            })
          );
        }

        await Promise.all(requests);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadContractDetails();

    return () => {
      cancelled = true;
    };
  }, [show, asset?.id, asset?.chassis, asset?.hasTruckerPayPlan, asset?.hasVolksEssentialsPlan]);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setContractDetails(null);
    }, CLOSE_ANIMATION_MS);
  };

  if (!show || !asset) return null;

  const details = contractDetails;
  const chassis = asset.chassis ?? "—";
  const contractNumber = details?.contract ?? "—";
  const contractType = details?.name ?? "—";
  const model = asset.model || "—";
  const licensePlate = asset.licensePlate ?? "—";
  const accountName = asset.accountName ?? "—";

  // Only show initial odometer/hourmeter if there's at least one product that uses them
  const initialOdometer = details?.products.filter(e => e.kilometers).length ? details?.initialOdometer : undefined;
  const initialHourMeter = details?.products.filter(e => e.hours).length ? details?.initialHourMeter : undefined;

  // Calculate contract duration in months based on start and end dates
  const contractDuration = details ? monthDifference(new Date(details.startAt), new Date(details.endAt)) : 0;

  const contractStatus = PlanStatusEnum[(details?.status ?? 'UNKNOWN') as keyof typeof PlanStatusEnum];
  const products = details?.products ?? [];

  const closingClassBg = isClosing ? " contract-details-bg--closing" : "";
  const closingClassSidebar = isClosing ? " contract-details-sidebar--closing" : "";

  return (
    <>
      <div
        className={`contract-details-bg${closingClassBg}`}
        onClick={handleClose}
        aria-hidden
      />
      <div
        className={`contract-details-sidebar${closingClassSidebar} ${isMobile() ? "!w-[90%]" : "!w-[70%]"} max-w-[830px]`}
      >
        <Header onClose={handleClose} />

        <div className="w-full margin-top-20">
          {loading ? (
            <Loading />
          ) : (
            <>
              {asset.hasTruckerPayPlan ? 
                <Info
                  chassis={chassis}
                  model={model}
                  contractDuration={contractDuration}
                  licensePlate={licensePlate}
                  contractNumber={contractNumber}
                  contractType={contractType}
                  initialOdometer={initialOdometer}
                  monthlyAverageKm={details?.products?.[0]?.monthlyAverageKilometers}
                  initialHourMeter={initialHourMeter}
                  monthlyAverageHr={details?.products?.[0]?.monthlyAverageHours}
                  accountName={accountName}
                  contractStatus={contractStatus}
                  createdAt={details?.createdAt}
                />
              : asset.hasVolksEssentialsPlan ? 
                <InfoPlanEssential
                  chassis={contractDetailsPlanEssential?.chassis || ''}
                  accountName={accountName}
                  contractType={contractDetailsPlanEssential?.contractType || ''}
                  licensePlate={contractDetailsPlanEssential?.licensePlate || ''}
                  model={contractDetailsPlanEssential?.modelName || ''}
                  contractNumber={contractDetailsPlanEssential?.contract || ''}
                  startAt={contractDetailsPlanEssential?.startAt}
                  endAt={contractDetailsPlanEssential?.endAt}
                  activationDate={contractDetailsPlanEssential?.activationDate}
                  odometer={contractDetailsPlanEssential?.odometer || ''}
                  hourMeter={contractDetailsPlanEssential?.hourMeter || ''}
                  contractStatus={contractDetailsPlanEssential?.contractStatus || ''}
                  hiddenFields={hiddenFields}
                />
              : null}

              <Tabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                hasTruckerPayPlan={asset.hasTruckerPayPlan}
                hasVolksEssentialsPlan={asset.hasVolksEssentialsPlan}
              />

              {asset.hasTruckerPayPlan ? (
                <>
                  {activeTab === "franquia" && (
                    <Franquia
                      products={products}
                      initialOdometer={initialOdometer}
                      currentOdometer={asset.odometer}
                      initialHourMeter={initialHourMeter}
                      currentHourmeter={asset.hourmeter}
                    />
                  )}

                  {activeTab === "saldo" && <CoverageBalance truckerPayPlan={details} />}
                </>
              ) : asset.hasVolksEssentialsPlan ? (
                <>
                  {activeTab === "revisoes" && 
                    <Revisions 
                      volksEssentialPlan={contractDetailsPlanEssential} 
                      updateMaintenanceGroupButtonAvailable={asset.groupName === '-' && userRole !== UserRoleEnum.MANAGER}
                      setMaintenanceGroupSidebarAsset={setMaintenanceGroupSidebarAsset}
                      setShowMaintenanceGroupSidebarDialog={setShowMaintenanceGroupSidebarDialog}
                      asset={asset}
                      closeModal={handleClose}
                    />}
                </>
              ) : null}
              
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ContractDetailsSidebar;
